import { countries, defaultTimeline } from './data';
import type {
  BiomarkerFeedback,
  CostEstimate,
  CostLine,
  CountryFit,
  GuidanceReport,
  JourneyLocale,
  MedicalInputs,
  RecommendedPathway,
  UserPreferences
} from './types';

const money = (value: number) => Math.round(value);

export function normalizeJourneyLocale(locale?: JourneyLocale | 'zh-Hant'): JourneyLocale {
  if (locale === 'en' || locale === 'vi') return locale;
  return 'zh-Hans';
}

export const validationCopy: Record<string, string> = {
  single_male_requires_surrogacy_or_review: '单身男性通常需要孕母承载；若不使用代孕，需先由顾问个案评估。',
  male_couple_requires_surrogacy_or_review: '双男伴侣通常需要孕母承载与供卵安排，需由律师确认亲权路径。',
  male_identity_cannot_self_provide_eggs: '男性身份设定不应选择自体或伴侣卵子，除非存在跨性别或特殊医疗背景。',
  female_couple_requires_sperm_source_review: '双女伴侣需确认捐精或其他精子来源，否则无法形成可执行实验室方案。',
  self_carrier_requires_uterus_review: '亲自或伴侣怀孕需要子宫与内膜条件评估。',
  existing_embryo_requires_embryo_data: '既有胚胎路径需补充胚胎数量、PGT 状态、冷冻地点与转运可行性。',
  sex_selection_requires_country_legal_filter: '性别或胎数偏好必须通过目的地法律与医疗伦理审核。',
  twin_preference_requires_medical_review: '双胎或龙凤胎愿景需医师先评估多胎妊娠风险；报告仅能作初步评估。',
  existing_embryo_goal_requires_embryo_source: '已选择既有胚胎移植愿景，生育资源需同步设定为已有胚胎。',
  unknown_goal_requires_consultant_scoping: '宝宝愿景尚未确定，建议先由顾问界定家庭目标、可行国家与预算范围。',
  other_lgbtq_requires_consultant_review: '其他或未分类多元家庭情境需要顾问先厘清身份、配子与承载配置。',
  unknown_resource_requires_review: '目前仍有配子或承载来源未确认，报告只能作为初步咨询入口。'
};

export const pathwayBadge: Record<RecommendedPathway, string> = {
  iui_or_ivf: 'IUI / IVF',
  ivf: 'IVF',
  icsi_ivf: 'ICSI-IVF',
  ropa: 'ROPA',
  donor_egg_ivf: 'Donor Egg IVF',
  embryo_transfer: 'Embryo Transfer',
  surrogacy_ivf: 'Surrogacy IVF',
  needs_review: 'Needs Review'
};

export function getValidationFlags(preferences: UserPreferences, medical?: MedicalInputs): string[] {
  const flags = new Set<string>();

  if (preferences.category === 'single_man' && preferences.carrier !== 'surrogate') {
    flags.add('single_male_requires_surrogacy_or_review');
  }

  if (preferences.category === 'male_couple' && preferences.carrier !== 'surrogate') {
    flags.add('male_couple_requires_surrogacy_or_review');
  }

  if (
    (preferences.category === 'single_man' || preferences.category === 'male_couple') &&
    (preferences.eggSource === 'self' || preferences.eggSource === 'partner')
  ) {
    flags.add('male_identity_cannot_self_provide_eggs');
  }

  if (preferences.category === 'female_couple' && preferences.spermSource === 'unknown') {
    flags.add('female_couple_requires_sperm_source_review');
  }

  if (preferences.category === 'other_lgbtq') {
    flags.add('other_lgbtq_requires_consultant_review');
  }

  if ([preferences.eggSource, preferences.spermSource, preferences.carrier].includes('unknown')) {
    flags.add('unknown_resource_requires_review');
  }

  if (preferences.existingEmbryo && preferences.eggSource !== 'existing_embryo') {
    flags.add('existing_embryo_goal_requires_embryo_source');
  }

  if (
    preferences.eggSource === 'existing_embryo' &&
    (!medical || !medical.embryoCount || preferences.embryoStatus === 'unknown' || !medical.embryoFreezeLocation || !medical.embryoTransportReady)
  ) {
    flags.add('existing_embryo_requires_embryo_data');
  }

  if (
    (preferences.carrier === 'self' || preferences.carrier === 'partner') &&
    (!medical ||
      medical.uterusStatus === 'unknown' ||
      medical.uterusStatus === 'abnormal' ||
      medical.uterusStatus === 'no_uterus' ||
      medical.endometriumStatus === 'thin' ||
      medical.endometriumStatus === 'unknown' ||
      medical.previousMiscarriage ||
      medical.previousFailedTransfer)
  ) {
    flags.add('self_carrier_requires_uterus_review');
  }

  if (preferences.sexSelectionRequested || preferences.genderPreference === 'son' || preferences.genderPreference === 'daughter') {
    flags.add('sex_selection_requires_country_legal_filter');
  }

  if (preferences.twinPreference || preferences.requiresMedicalReview) {
    flags.add('twin_preference_requires_medical_review');
  }

  if (preferences.goalUnknown) {
    flags.add('unknown_goal_requires_consultant_scoping');
  }

  return [...flags];
}

export function deriveRecommendedPathway(preferences: UserPreferences, medical?: MedicalInputs): RecommendedPathway {
  const blockingFlags = getValidationFlags(preferences, medical).filter((flag) =>
    [
      'single_male_requires_surrogacy_or_review',
      'male_couple_requires_surrogacy_or_review',
      'male_identity_cannot_self_provide_eggs',
      'unknown_resource_requires_review',
      'existing_embryo_goal_requires_embryo_source',
      'other_lgbtq_requires_consultant_review'
    ].includes(flag)
  );

  if (blockingFlags.length > 0) return 'needs_review';
  if (preferences.eggSource === 'existing_embryo') return 'embryo_transfer';
  if (preferences.carrier === 'surrogate') return 'surrogacy_ivf';
  if (preferences.category === 'female_couple' && preferences.carrier === 'partner') return 'ropa';
  if (preferences.eggSource === 'donor') return 'donor_egg_ivf';
  if (medical?.spermMotility === 'low' || medical?.spermMotility === 'borderline') return 'icsi_ivf';
  if (preferences.category === 'single_woman' && preferences.spermSource === 'donor') return 'iui_or_ivf';
  return 'ivf';
}

const missingDataLabels: Record<JourneyLocale, Record<string, string>> = {
  'zh-Hans': {
    age: '取卵者年龄',
    amh: 'AMH',
    fsh: 'FSH',
    afc: 'AFC 基础卵泡数',
    semenAnalysis: '精液分析 / 精子活力',
    concentration: '精液浓度',
    morphology: '精子形态',
    uterus: '子宫与内膜承载评估',
    endometrium: '内膜状态',
    embryoCount: '胚胎数量',
    embryoPgt: '胚胎 PGT 状态',
    embryoLocation: '胚胎冷冻地点',
    embryoTransport: '胚胎转运可行性'
  },
  en: {
    age: 'Oocyte provider age',
    amh: 'AMH',
    fsh: 'FSH',
    afc: 'AFC antral follicle count',
    semenAnalysis: 'Semen analysis / sperm motility',
    concentration: 'Sperm concentration',
    morphology: 'Sperm morphology',
    uterus: 'Uterus and endometrium carrier assessment',
    endometrium: 'Endometrium status',
    embryoCount: 'Embryo count',
    embryoPgt: 'Embryo PGT status',
    embryoLocation: 'Embryo storage location',
    embryoTransport: 'Embryo transport feasibility'
  },
  vi: {
    age: 'Tuổi người cung cấp trứng',
    amh: 'AMH',
    fsh: 'FSH',
    afc: 'AFC số nang thứ cấp',
    semenAnalysis: 'Phân tích tinh dịch / độ di động tinh trùng',
    concentration: 'Nồng độ tinh trùng',
    morphology: 'Hình thái tinh trùng',
    uterus: 'Đánh giá tử cung và niêm mạc',
    endometrium: 'Tình trạng niêm mạc',
    embryoCount: 'Số lượng phôi',
    embryoPgt: 'Tình trạng PGT của phôi',
    embryoLocation: 'Nơi lưu trữ phôi',
    embryoTransport: 'Khả năng vận chuyển phôi'
  }
};

export function getMissingData(preferences: UserPreferences, medical: MedicalInputs, locale: JourneyLocale = 'zh-Hans'): string[] {
  const missing: string[] = [];
  const labels = missingDataLabels[locale];
  const needsEgg = preferences.eggSource === 'self' || preferences.eggSource === 'partner';
  const needsSperm = preferences.spermSource === 'self' || preferences.spermSource === 'partner';
  const needsCarrier = preferences.carrier === 'self' || preferences.carrier === 'partner';

  if (needsEgg) {
    if (medical.age === null) missing.push(labels.age);
    if (medical.amh === null) missing.push(labels.amh);
    if (medical.fsh === null) missing.push(labels.fsh);
    if (medical.afc === null) missing.push(labels.afc);
  }

  if (needsSperm && medical.spermMotility === 'unknown') {
    missing.push(labels.semenAnalysis);
  }
  if (needsSperm && medical.spermConcentration === null) {
    missing.push(labels.concentration);
  }
  if (needsSperm && medical.spermMorphology === null) {
    missing.push(labels.morphology);
  }

  if (needsCarrier && medical.uterusStatus === 'unknown') {
    missing.push(labels.uterus);
  }
  if (needsCarrier && medical.endometriumStatus === 'unknown') {
    missing.push(labels.endometrium);
  }

  if (preferences.eggSource === 'existing_embryo') {
    if (!medical.embryoCount) missing.push(labels.embryoCount);
    if (preferences.embryoStatus === 'unknown') missing.push(labels.embryoPgt);
    if (!medical.embryoFreezeLocation) missing.push(labels.embryoLocation);
    if (!medical.embryoTransportReady) missing.push(labels.embryoTransport);
  }

  return missing;
}

export function evaluateBiomarkers(preferences: UserPreferences, medical: MedicalInputs): BiomarkerFeedback[] {
  const feedback: BiomarkerFeedback[] = [];
  const needsEgg = preferences.eggSource === 'self' || preferences.eggSource === 'partner';
  const needsSperm = preferences.spermSource === 'self' || preferences.spermSource === 'partner';
  const needsCarrier = preferences.carrier === 'self' || preferences.carrier === 'partner';

  if (needsEgg) {
    feedback.push(
      medical.age === null
        ? {
            label: '取卵者年龄',
            value: '未填写',
            status: 'unknown',
            text: '尚未提供取卵者年龄',
            advice: '供卵或自卵方案需由医疗团队结合取卵者年龄、筛查与卵巢储备评估。'
          }
        : medical.age < 35
        ? {
            label: '取卵者年龄',
            value: `${medical.age} 岁`,
            status: 'optimal',
            text: '年龄窗口相对友善',
            advice: '可优先以自卵或伴侣卵子建立胚胎库，再按家庭计划安排移植。'
          }
        : medical.age <= 39
          ? {
              label: '取卵者年龄',
              value: `${medical.age} 岁`,
              status: 'watch',
              text: '需重视胚胎染色体比例',
              advice: '建议把 PGT-A、胚胎储备与多周期预算纳入初始方案。'
            }
          : {
              label: '取卵者年龄',
              value: `${medical.age} 岁`,
              status: 'critical',
              text: '高龄自卵成功率受限',
              advice: '需温和说明自卵限制，并同步准备供卵或胚胎来源备案。'
            }
    );

    feedback.push(
      medical.amh === null
        ? {
            label: 'AMH 卵巢储备',
            value: '未检测',
            status: 'unknown',
            text: '尚未提供 AMH 数值',
            advice: '建议先完成 AMH 与基础卵泡数检查，再做促排与取卵预算。'
          }
        : medical.amh >= 2
          ? {
              label: 'AMH 卵巢储备',
              value: `${medical.amh} ng/mL`,
              status: 'optimal',
              text: '满意且正常之储备量',
              advice: '预期单次取卵促排即可顺利培育出数颗符合优选标准的理想囊胚。'
            }
          : medical.amh >= 1
            ? {
                label: 'AMH 卵巢储备',
                value: `${medical.amh} ng/mL`,
                status: 'normal',
                text: '正常但需精准促排',
                advice: '可采标准或微调促排方案，并根据年龄与胚胎结果决定是否胚胎储备。'
              }
            : {
                label: 'AMH 卵巢储备',
                value: `${medical.amh} ng/mL`,
                status: 'watch',
                text: '储备量偏低',
                advice: '可考虑多次取卵累积、低刺激方案或提前讨论第三方卵子备案。'
              }
    );

    feedback.push(
      medical.fsh === null
        ? {
            label: 'FSH 促卵泡成熟素',
            value: '未检测',
            status: 'unknown',
            text: '缺少基础内分泌指标',
            advice: '建议于月经第 2-3 天同步检测 FSH、LH、E2，以免单一数值误判。'
          }
        : medical.fsh <= 9
          ? {
              label: 'FSH 促卵泡成熟素',
              value: `${medical.fsh} mIU/mL`,
              status: 'normal',
              text: '基础 FSH 反馈稳定',
              advice: '可与 AMH、AFC 与年龄一起解读，作为促排起始剂量参考。'
            }
          : {
              label: 'FSH 促卵泡成熟素',
              value: `${medical.fsh} mIU/mL`,
              status: 'watch',
              text: 'FSH 需复核',
              advice: '需结合 E2 与超音波，避免被暂时性内分泌波动误导。'
            }
    );

    feedback.push({
      label: 'AFC 基础卵泡数',
      value: medical.afc === null ? '未检测' : `${medical.afc}`,
      status: medical.afc === null ? 'unknown' : medical.afc >= 8 ? 'normal' : 'watch',
      text: medical.afc === null ? '缺少超音波基础卵泡数' : '可作为促排反应与取卵预估参考',
      advice: 'AFC 需与 AMH、年龄、既往促排反应共同判读。'
    });
  } else if (preferences.eggSource === 'donor') {
    feedback.push({
      label: '卵子来源',
      value: '第三方供卵',
      status: 'normal',
      text: '不要求填写使用者 AMH / FSH / AFC',
      advice: '重点转向供卵者年龄、基因筛查、法律同意与配型透明度。'
    });
  } else if (preferences.eggSource === 'existing_embryo') {
    feedback.push({
      label: '既有胚胎',
      value: medical.embryoCount ? `${medical.embryoCount} 枚` : '数量未填',
      status: medical.embryoCount ? 'normal' : 'unknown',
      text: '以胚胎资料完整性作为可行性核心',
      advice: '需补齐 Day 5/6、PGT 状态、冷冻地点、转运与解冻风险资料。'
    });
  }

  if (needsSperm) {
    feedback.push({
      label: '精子活力',
      value:
        medical.spermMotility === 'normal'
          ? '正常'
          : medical.spermMotility === 'borderline'
            ? '微退化'
            : medical.spermMotility === 'low'
              ? '活力受限'
              : '未作检验',
      status:
        medical.spermMotility === 'normal'
          ? 'normal'
          : medical.spermMotility === 'low'
            ? 'critical'
            : medical.spermMotility === 'borderline'
              ? 'watch'
              : 'unknown',
      text: '精子品质与活跃度分析评级',
      advice:
        medical.spermMotility === 'low'
          ? '建议评估 ICSI、精子 DNA 碎片率与必要时单精子冷冻策略。'
          : medical.spermMotility === 'borderline'
            ? '可配合 ICSI 与精液优化处理，提升受精稳定性。'
            : medical.spermMotility === 'normal'
              ? '精液条件对 IUI 或 IVF 可行性均有正面支持。'
              : '模型将暂以基准常规比率代入演算，仍建议补齐精液分析。'
    });
    feedback.push({
      label: '精液浓度 / 形态',
      value: `${medical.spermConcentration ?? '未填'} M/mL | ${medical.spermMorphology ?? '未填'}%`,
      status:
        medical.spermConcentration === null || medical.spermMorphology === null
          ? 'unknown'
          : medical.spermConcentration >= 15 && medical.spermMorphology >= 4
            ? 'normal'
            : 'watch',
      text: '精液浓度与形态用于判断 IUI、IVF 或 ICSI 起步',
      advice: '若浓度或形态偏低，需由胚胎实验室评估 ICSI、DNA 碎片率与精液优化。'
    });
  } else if (preferences.spermSource === 'donor') {
    feedback.push({
      label: '精子来源',
      value: '认证捐精',
      status: 'normal',
      text: '不要求填写使用者精液报告',
      advice: '重点转向精子库资质、基因筛查、运输文件与亲权同意。'
    });
  }

  if (needsCarrier) {
    feedback.push({
      label: '妊娠承载',
      value:
        medical.uterusStatus === 'normal'
          ? '子宫条件稳定'
          : medical.uterusStatus === 'abnormal'
            ? '需妇科复核'
            : medical.uterusStatus === 'no_uterus'
              ? '无子宫承载'
              : '尚未评估',
      status:
        medical.uterusStatus === 'normal'
          ? 'normal'
          : medical.uterusStatus === 'abnormal' || medical.uterusStatus === 'no_uterus'
            ? 'watch'
            : 'unknown',
      text: '承载方式需配合子宫、内膜与既往移植资料',
      advice: '如有反复流产或移植失败，应先完成子宫镜、内膜与免疫相关医师评估。'
    });
    feedback.push({
      label: '内膜条件',
      value:
        medical.endometriumStatus === 'ready'
          ? '内膜准备稳定'
          : medical.endometriumStatus === 'thin'
            ? '内膜偏薄或需复核'
            : '尚未评估',
      status: medical.endometriumStatus === 'ready' ? 'normal' : medical.endometriumStatus === 'thin' ? 'watch' : 'unknown',
      text: '移植前需确认内膜厚度、型态与荷尔蒙准备',
      advice: '若内膜偏薄或曾移植失败，需先完成用药方案与子宫镜/内膜评估。'
    });
  } else if (preferences.carrier === 'surrogate') {
    feedback.push({
      label: '妊娠承载',
      value: '孕母承载',
      status: 'normal',
      text: '不要求使用者子宫承载评估',
      advice: '重点转向孕母匹配、心理评估、保险、托管信托与亲权文件。'
    });
  }

  return feedback;
}

export function rankCountries(preferences: UserPreferences, validationFlags: string[]): CountryFit[] {
  return countries.map((country) => {
    const reasons: string[] = [];
    const categoryAllowed = preferences.category ? country.allowedCategories.includes(preferences.category) : false;
    const needsSurrogacy = preferences.carrier === 'surrogate';
    const needsLegalReview = validationFlags.some((flag) =>
      [
        'sex_selection_requires_country_legal_filter',
        'twin_preference_requires_medical_review',
        'self_carrier_requires_uterus_review',
        'unknown_goal_requires_consultant_scoping'
      ].includes(flag)
    );
    const hasBlockingReview = validationFlags.some((flag) =>
      [
        'single_male_requires_surrogacy_or_review',
        'male_couple_requires_surrogacy_or_review',
        'male_identity_cannot_self_provide_eggs',
        'unknown_resource_requires_review',
        'other_lgbtq_requires_consultant_review'
      ].includes(flag)
    );

    if (!categoryAllowed) reasons.push('身份类型准入受限');
    if (needsSurrogacy && !country.surrogacyAllowed) reasons.push('代孕路径不可用');
    if ((preferences.sexSelectionRequested || preferences.twinPreference) && !country.genderSelectionAllowed) reasons.push('胚胎性别或胎数配置受限');
    if (preferences.eggSource === 'donor' && !country.eggDonationAllowed) reasons.push('供卵准入受限');
    if (preferences.spermSource === 'donor' && !country.spermDonationAllowed) reasons.push('供精准入受限');

    const hasAccessBlock = reasons.includes('身份类型准入受限') || reasons.includes('代孕路径不可用');
    const status: CountryFit['status'] = hasAccessBlock
      ? 'not_recommended'
      : reasons.length > 0
        ? 'restricted'
        : hasBlockingReview || needsLegalReview
          ? 'needs_review'
          : 'allowed';

    return {
      code: country.code,
      country: country.country,
      enName: country.enName,
      flag: country.flag,
      status,
      averageCostUSD: country.averageCostUSD,
      reasons: reasons.length ? reasons : status === 'needs_review' ? ['需顾问或律师复核'] : ['同等规则下可比较'],
      summary: country.summary,
      legalNote: country.legalNote
    };
  });
}

export function estimateCosts(preferences: UserPreferences, pathway: RecommendedPathway, preferredCountryCost = 145000): CostEstimate {
  const lines: CostLine[] = [];

  if (pathway === 'iui_or_ivf') {
    lines.push({
      label: 'IUI / IVF 初阶决策与实验室预算',
      category: 'medical',
      amountUSD: 8500,
      description: '由医师根据年龄、输卵管、精液与时间成本决定 IUI 或 IVF 起步。'
    });
  } else if (pathway === 'embryo_transfer') {
    lines.push({
      label: '既有胚胎解冻、转运与移植',
      category: 'medical',
      amountUSD: 9800,
      description: '胚胎资料审核、冷链转运、解冻与移植周期安排。'
    });
  } else {
    lines.push({
      label: '常规试管婴儿（IVF）实验室周期',
      category: 'medical',
      amountUSD: pathway === 'icsi_ivf' ? 13500 : 11525,
      description: pathway === 'icsi_ivf' ? '含单精子注射、精液优化与囊胚培养。' : '促排、取卵、受精与囊胚培养基础费用。'
    });
  }

  lines.push({
    label: '胚胎植入前遗传学筛检（PGT-A）',
    category: 'medical',
    amountUSD: preferences.sexSelectionRequested || preferences.twinPreference ? 7000 : 5200,
    description: '染色体筛查与合法地区的胚胎配置参考。'
  });

  if (preferences.twinPreference) {
    lines.push({
      label: '双胎愿景医疗安全复核',
      category: 'medical',
      amountUSD: 2800,
      description: '多胎妊娠风险、单胚胎移植替代方案与医师安全审核。'
    });
  }

  lines.push({
    label: '法律合同、公证与亲权文件',
    category: 'legal',
    amountUSD: preferences.carrier === 'surrogate' ? 18000 : 6500,
    description: '多边合约、出生亲权、法院文件与返国文件审核。'
  });

  if (preferences.eggSource === 'donor') {
    lines.push({
      label: '第三方卵子供应匹配与医学补偿',
      category: 'third_party',
      amountUSD: 18500,
      description: '供卵者筛查、补偿、法律文件与周期管理。'
    });
  }

  if (preferences.spermSource === 'donor') {
    lines.push({
      label: '经认证低温库捐精采购',
      category: 'third_party',
      amountUSD: 2200,
      description: '精子库样本、运输与基因相容性文件。'
    });
  }

  if (preferences.carrier === 'surrogate') {
    lines.push(
      {
        label: '孕母匹配、心理评估与机构管理',
        category: 'agency',
        amountUSD: 22500,
        description: '孕母背景、心理健康与生殖专科身心健筛。'
      },
      {
        label: '爱心孕母补偿与妊娠专属医疗保险',
        category: 'third_party',
        amountUSD: 40000,
        description: '孕期补偿、生活津贴、医疗保险与意外支出预留。'
      }
    );
  }

  if (pathway === 'needs_review') {
    lines.push({
      label: '顾问个案路径厘清与文件初审',
      category: 'agency',
      amountUSD: 3500,
      description: '针对身份、配子来源、承载方式与目的地法律的初步审核。'
    });
  }

  lines.push({
    label: '国际差旅、翻译与文件递送',
    category: 'travel',
    amountUSD: 8500,
    description: '初诊、取卵、移植或出生文件阶段的家庭差旅配置。'
  });

  const lineTotal = lines.reduce((sum, line) => sum + line.amountUSD, 0);
  const medianBase = Math.max(lineTotal, preferredCountryCost * (preferences.carrier === 'surrogate' ? 0.72 : 0.42));
  const medianUSD = money(medianBase * (preferences.twinPreference ? 1.08 : 1));

  return {
    baselineCountry: '可比较国家中位预算',
    medianUSD,
    lowUSD: money(medianUSD * 0.85),
    highUSD: money(medianUSD * 1.15),
    lines
  };
}

export function buildGuidanceReport(preferences: UserPreferences, medical: MedicalInputs): GuidanceReport {
  return buildGuidanceReportForLocale(preferences, medical, 'zh-Hans');
}

const countryText: Record<JourneyLocale, Record<string, { country: string; summary: string; legalNote: string }>> = {
  'zh-Hans': {},
  en: {
    US: {
      country: 'United States',
      summary: 'Highest legal-protection standard. Many family-friendly states support secure parentage routes regardless of marital status or orientation.',
      legalNote: 'Pre-birth parentage orders and escrow structures can often establish parentage directly, but state-by-state counsel review is required.'
    },
    CO: {
      country: 'Colombia',
      summary: 'Anti-discrimination protections make Colombia relatively friendly for LGBTQ+ and single intended parents, with lower costs than the United States.',
      legalNote: 'Birth registration and parentage documents should be arranged in advance with local counsel.'
    },
    GE: {
      country: 'Georgia',
      summary: 'Attractive cost and mature medical infrastructure, but access is narrow and mainly limited to heterosexual married couples.',
      legalNote: 'Single and LGBTQ+ pathways usually do not apply and should not be recommended as a general route.'
    },
    GR: {
      country: 'Greece',
      summary: 'A structured European framework. Single women and heterosexual couples may proceed after court approval.',
      legalNote: 'Court approval before transfer is the key milestone; LGBTQ+ partner access remains limited.'
    },
    MX: {
      country: 'Mexico',
      summary: 'Lower cost and close to the United States, but state selection, clinic choice, and legal route quality are critical.',
      legalNote: 'State differences are significant; parentage security needs local attorney confirmation.'
    },
    CY: {
      country: 'Cyprus',
      summary: 'Strong for conventional IVF, donor eggs, and reciprocal IVF at lower cost, but not a secure surrogacy parentage route.',
      legalNote: 'Best considered as an IVF or donor center, not as a primary surrogacy parentage jurisdiction.'
    },
    TW: {
      country: 'Taiwan',
      summary: 'Advanced medical quality, but assisted reproduction access is mainly limited to legally married heterosexual couples.',
      legalNote: 'Single, surrogacy, and diverse-family routes are not open.'
    },
    TH: {
      country: 'Thailand',
      summary: 'A trusted Asian reproductive medicine hub for IVF, PGT, egg retrieval, and cryopreservation.',
      legalNote: 'Foreign commercial surrogacy should not be treated as a Thailand-local route; gamete and marriage documentation must be compliant.'
    },
    MY: {
      country: 'Malaysia',
      summary: 'Mature medical facilities with Chinese and English service support, suitable for conventional IVF budget planning.',
      legalNote: 'Diverse-family and surrogacy access requires caution and is usually not the main parentage-security route.'
    }
  },
  vi: {
    US: {
      country: 'Hoa Kỳ',
      summary: 'Tiêu chuẩn bảo vệ pháp lý cao nhất. Nhiều bang thân thiện hỗ trợ lộ trình quyền cha mẹ an toàn cho nhiều nhóm gia đình.',
      legalNote: 'Lệnh quyền cha mẹ trước sinh và tài khoản ký quỹ thường có thể xác lập quyền cha mẹ, nhưng cần luật sư rà soát theo từng bang.'
    },
    CO: {
      country: 'Colombia',
      summary: 'Nhờ luật chống phân biệt đối xử, Colombia tương đối thân thiện với LGBTQ+ và cha mẹ đơn thân, chi phí thấp hơn Hoa Kỳ.',
      legalNote: 'Đăng ký khai sinh và hồ sơ quyền cha mẹ cần được luật sư địa phương chuẩn bị trước.'
    },
    GE: {
      country: 'Georgia',
      summary: 'Chi phí và năng lực y tế hấp dẫn, nhưng điều kiện tiếp cận hẹp, chủ yếu dành cho vợ chồng khác giới đã kết hôn.',
      legalNote: 'Lộ trình độc thân và LGBTQ+ thường không phù hợp, không nên dùng như khuyến nghị chung.'
    },
    GR: {
      country: 'Hy Lạp',
      summary: 'Khung pháp lý châu Âu tương đối rõ. Phụ nữ độc thân và cặp đôi khác giới có thể thực hiện sau khi tòa chấp thuận.',
      legalNote: 'Phê duyệt của tòa trước chuyển phôi là mốc quan trọng; cặp đôi LGBTQ+ vẫn có nhiều hạn chế.'
    },
    MX: {
      country: 'Mexico',
      summary: 'Gần Hoa Kỳ và chi phí thấp hơn, nhưng cần chọn bang, phòng khám và tuyến pháp lý có bảo vệ tốt.',
      legalNote: 'Khác biệt giữa các bang rất lớn; an toàn quyền cha mẹ cần luật sư địa phương xác nhận.'
    },
    CY: {
      country: 'Síp',
      summary: 'Phù hợp cho IVF thường quy, trứng hiến tặng và IVF hai mẹ với chi phí thấp, nhưng không phải tuyến mang thai hộ an toàn.',
      legalNote: 'Nên xem là trung tâm IVF/hiến trứng, không phải lộ trình chính cho mang thai hộ.'
    },
    TW: {
      country: 'Đài Loan',
      summary: 'Y tế chất lượng cao, nhưng điều kiện hỗ trợ sinh sản chủ yếu giới hạn cho vợ chồng khác giới hợp pháp.',
      legalNote: 'Độc thân, mang thai hộ và gia đình đa dạng không mở rộng.'
    },
    TH: {
      country: 'Thái Lan',
      summary: 'Trung tâm y học sinh sản châu Á đáng tin cậy cho IVF, PGT, lấy trứng và trữ lạnh.',
      legalNote: 'Mang thai hộ thương mại cho người nước ngoài không nên xem là lộ trình nội địa Thái Lan; hồ sơ giao tử và hôn nhân phải hợp lệ.'
    },
    MY: {
      country: 'Malaysia',
      summary: 'Cơ sở y tế trưởng thành, dịch vụ tiếng Trung/Anh thuận tiện, phù hợp cho IVF thường quy và ngân sách vừa phải.',
      legalNote: 'Gia đình đa dạng và mang thai hộ cần thận trọng, thường không phải tuyến chính để bảo vệ quyền cha mẹ.'
    }
  }
};

const reasonText: Record<JourneyLocale, Record<string, string>> = {
  'zh-Hans': {},
  en: {
    身份类型准入受限: 'Identity category access is limited',
    代孕路径不可用: 'Surrogacy route is unavailable',
    胚胎性别或胎数配置受限: 'Embryo sex or fetal-number configuration is restricted',
    供卵准入受限: 'Donor egg access is restricted',
    供精准入受限: 'Donor sperm access is restricted',
    需顾问或律师复核: 'Advisor or attorney review required',
    准入合规: 'Access appears compliant',
    同等规则下可比较: 'Comparable under the same criteria'
  },
  vi: {
    身份类型准入受限: 'Nhóm thân phận bị hạn chế tiếp cận',
    代孕路径不可用: 'Lộ trình mang thai hộ không khả dụng',
    胚胎性别或胎数配置受限: 'Cấu hình giới tính phôi hoặc số thai bị hạn chế',
    供卵准入受限: 'Tiếp cận trứng hiến tặng bị hạn chế',
    供精准入受限: 'Tiếp cận tinh trùng hiến tặng bị hạn chế',
    需顾问或律师复核: 'Cần cố vấn hoặc luật sư rà soát',
    准入合规: 'Tiếp cận có vẻ phù hợp',
    同等规则下可比较: 'Có thể so sánh theo cùng tiêu chí'
  }
};

const feedbackText: Record<JourneyLocale, Record<string, Partial<BiomarkerFeedback>>> = {
  'zh-Hans': {},
  en: {
    取卵者年龄: { label: 'Oocyte provider age', text: 'Age window review', advice: 'Use age together with AMH, AFC, and embryo results to plan self/partner egg strategy.' },
    'AMH 卵巢储备': { label: 'AMH ovarian reserve', text: 'Ovarian reserve indicator', advice: 'Combine AMH with AFC, age, and prior response before estimating stimulation and retrieval budget.' },
    'FSH 促卵泡成熟素': { label: 'FSH baseline endocrine marker', text: 'Baseline FSH feedback', advice: 'Interpret with E2, AFC, AMH, and cycle day to avoid over-reading one value.' },
    'AFC 基础卵泡数': { label: 'AFC antral follicle count', text: 'Ultrasound follicle count reference', advice: 'AFC should be read with AMH, age, and prior stimulation response.' },
    卵子来源: { label: 'Egg source', value: 'Donor eggs', text: 'User AMH / FSH / AFC are not required', advice: 'Focus shifts to donor age, genetic screening, legal consent, and matching transparency.' },
    既有胚胎: { label: 'Existing embryos', text: 'Embryo record completeness is the feasibility core', advice: 'Confirm Day 5/6, PGT status, storage location, transport, and thaw risk.' },
    精子活力: { label: 'Sperm motility', text: 'Sperm quality and motility classification', advice: 'Low or borderline results should be reviewed for ICSI, DNA fragmentation, and lab optimization.' },
    '精液浓度 / 形态': { label: 'Sperm concentration / morphology', text: 'Concentration and morphology inform IUI, IVF, or ICSI start point', advice: 'If concentration or morphology is low, embryology should review ICSI and sperm optimization.' },
    精子来源: { label: 'Sperm source', value: 'Certified donor sperm', text: 'User semen report is not required', advice: 'Focus shifts to sperm bank credentials, genetic screening, transport, and parentage consent.' },
    妊娠承载: { label: 'Gestational carrier', text: 'The intended father does not need to provide uterus or endometrium data', advice: 'Focus on the carrier’s medical and psychological screening, insurance, escrow, and parentage documents.' },
    内膜条件: { label: 'Endometrium readiness', text: 'Confirm thickness, pattern, and hormone preparation before transfer', advice: 'Thin lining or failed transfers need medication and uterine/endometrial review.' }
  },
  vi: {
    取卵者年龄: { label: 'Tuổi người cung cấp trứng', text: 'Đánh giá cửa sổ tuổi', advice: 'Kết hợp tuổi với AMH, AFC và kết quả phôi để lập chiến lược trứng tự thân/bạn đời.' },
    'AMH 卵巢储备': { label: 'AMH dự trữ buồng trứng', text: 'Chỉ số dự trữ buồng trứng', advice: 'Kết hợp AMH với AFC, tuổi và đáp ứng kích trứng trước khi dự toán.' },
    'FSH 促卵泡成熟素': { label: 'FSH nội tiết nền', text: 'Phản hồi FSH nền', advice: 'Diễn giải cùng E2, AFC, AMH và ngày chu kỳ để tránh đọc sai một chỉ số.' },
    'AFC 基础卵泡数': { label: 'AFC số nang thứ cấp', text: 'Tham chiếu nang trên siêu âm', advice: 'AFC cần đọc cùng AMH, tuổi và đáp ứng kích trứng trước đây.' },
    卵子来源: { label: 'Nguồn trứng', value: 'Trứng hiến tặng', text: 'Không cần AMH / FSH / AFC của khách hàng', advice: 'Trọng tâm chuyển sang tuổi người hiến, sàng lọc gene, đồng ý pháp lý và ghép chọn.' },
    既有胚胎: { label: 'Phôi đã có', text: 'Độ đầy đủ hồ sơ phôi là trọng tâm khả thi', advice: 'Xác nhận Day 5/6, PGT, nơi lưu trữ, vận chuyển và rủi ro rã đông.' },
    精子活力: { label: 'Độ di động tinh trùng', text: 'Phân loại chất lượng và độ di động tinh trùng', advice: 'Kết quả thấp hoặc ranh giới nên được đánh giá ICSI, phân mảnh DNA và tối ưu labo.' },
    '精液浓度 / 形态': { label: 'Nồng độ / hình thái tinh trùng', text: 'Nồng độ và hình thái giúp quyết định IUI, IVF hoặc ICSI', advice: 'Nếu nồng độ hoặc hình thái thấp, labo phôi nên đánh giá ICSI và tối ưu tinh trùng.' },
    精子来源: { label: 'Nguồn tinh trùng', value: 'Tinh trùng hiến tặng được chứng nhận', text: 'Không cần báo cáo tinh dịch của khách hàng', advice: 'Trọng tâm chuyển sang ngân hàng tinh trùng, sàng lọc gene, vận chuyển và đồng ý quyền cha mẹ.' },
    妊娠承载: { label: 'Người mang thai hộ', text: 'Người cha dự định không cần cung cấp dữ liệu tử cung hoặc niêm mạc', advice: 'Tập trung vào sàng lọc y khoa và tâm lý của người mang thai, bảo hiểm, ký quỹ và hồ sơ quyền cha mẹ.' },
    内膜条件: { label: 'Sẵn sàng niêm mạc', text: 'Cần xác nhận độ dày, hình thái và chuẩn bị hormone trước chuyển phôi', advice: 'Niêm mạc mỏng hoặc từng thất bại cần rà soát thuốc và tử cung/niêm mạc.' }
  }
};

const valueText: Record<JourneyLocale, Record<string, string>> = {
  'zh-Hans': {},
  en: {
    未检测: 'Not tested',
    未填: 'Not provided',
    正常: 'Normal',
    微退化: 'Borderline',
    活力受限: 'Low motility',
    未作检验: 'Not tested',
    第三方供卵: 'Donor eggs',
    认证捐精: 'Certified donor sperm',
    孕母承载: 'Gestational carrier',
    子宫条件稳定: 'Uterus appears stable',
    需妇科复核: 'Gynecology review needed',
    无子宫承载: 'No uterine carrier',
    尚未评估: 'Not assessed',
    内膜准备稳定: 'Endometrium ready',
    内膜偏薄或需复核: 'Thin lining or needs review',
    数量未填: 'Embryo count not provided'
  },
  vi: {
    未检测: 'Chưa xét nghiệm',
    未填: 'Chưa cung cấp',
    正常: 'Bình thường',
    微退化: 'Ranh giới',
    活力受限: 'Di động thấp',
    未作检验: 'Chưa xét nghiệm',
    第三方供卵: 'Trứng hiến tặng',
    认证捐精: 'Tinh trùng hiến tặng được chứng nhận',
    孕母承载: 'Người mang thai hộ',
    子宫条件稳定: 'Tử cung ổn định',
    需妇科复核: 'Cần bác sĩ phụ khoa rà soát',
    无子宫承载: 'Không có tử cung để mang thai',
    尚未评估: 'Chưa đánh giá',
    内膜准备稳定: 'Niêm mạc sẵn sàng',
    内膜偏薄或需复核: 'Niêm mạc mỏng hoặc cần rà soát',
    数量未填: 'Chưa cung cấp số lượng phôi'
  }
};

const costText: Record<JourneyLocale, Record<string, { label: string; description: string }>> = {
  'zh-Hans': {},
  en: {
    'IUI / IVF 初阶决策与实验室预算': { label: 'IUI / IVF decision and lab budget', description: 'Physician decides IUI or IVF based on age, tubes, semen factors, and time cost.' },
    '既有胚胎解冻、转运与移植': { label: 'Existing embryo thaw, transport, and transfer', description: 'Embryo records, cold-chain transport, thaw, and transfer-cycle planning.' },
    '常规试管婴儿（IVF）实验室周期': { label: 'Classical IVF laboratory cycle', description: 'Stimulation, retrieval, fertilization, and blastocyst culture base cost.' },
    '胚胎植入前遗传学筛检（PGT-A）': { label: 'Preimplantation genetic testing (PGT-A)', description: 'Chromosome screening and lawful embryo-configuration reference where allowed.' },
    '双胎愿景医疗安全复核': { label: 'Twin-goal medical safety review', description: 'Multiple pregnancy risk, single embryo alternative, and physician safety review.' },
    '法律合同、公证与亲权文件': { label: 'Legal contracts, notarization, and parentage documents', description: 'Multi-party agreements, birth parentage, court files, and return documentation.' },
    '第三方卵子供应匹配与医学补偿': { label: 'Donor egg matching and medical compensation', description: 'Donor screening, compensation, legal documents, and cycle management.' },
    '经认证低温库捐精采购': { label: 'Certified cryobank donor sperm purchase', description: 'Sperm bank sample, transport, and genetic compatibility documents.' },
    '孕母匹配、心理评估与机构管理': { label: 'Carrier matching, psychological review, and agency management', description: 'Carrier background, mental health, and reproductive specialist screening.' },
    '爱心孕母补偿与妊娠专属医疗保险': { label: 'Carrier compensation and pregnancy-specific insurance', description: 'Pregnancy compensation, living allowance, medical insurance, and reserve costs.' },
    '顾问个案路径厘清与文件初审': { label: 'Advisor route scoping and document pre-review', description: 'Initial review of identity, gamete source, carrier, and destination law.' },
    '国际差旅、翻译与文件递送': { label: 'International travel, translation, and document delivery', description: 'Travel for consult, retrieval, transfer, or birth-document stages.' }
  },
  vi: {
    'IUI / IVF 初阶决策与实验室预算': { label: 'Ngân sách quyết định IUI / IVF và labo', description: 'Bác sĩ quyết định IUI hay IVF dựa trên tuổi, vòi trứng, tinh dịch và thời gian.' },
    '既有胚胎解冻、转运与移植': { label: 'Rã đông, vận chuyển và chuyển phôi đã có', description: 'Rà soát hồ sơ phôi, vận chuyển lạnh, rã đông và chu kỳ chuyển phôi.' },
    '常规试管婴儿（IVF）实验室周期': { label: 'Chu kỳ labo IVF thường quy', description: 'Kích trứng, lấy trứng, thụ tinh và nuôi phôi nang.' },
    '胚胎植入前遗传学筛检（PGT-A）': { label: 'Sàng lọc di truyền tiền làm tổ (PGT-A)', description: 'Sàng lọc nhiễm sắc thể và tham chiếu cấu hình phôi nơi pháp luật cho phép.' },
    '双胎愿景医疗安全复核': { label: 'Rà soát an toàn y khoa cho mục tiêu song thai', description: 'Rủi ro đa thai, phương án chuyển một phôi và đánh giá của bác sĩ.' },
    '法律合同、公证与亲权文件': { label: 'Hợp đồng, công chứng và hồ sơ quyền cha mẹ', description: 'Hợp đồng nhiều bên, quyền cha mẹ khi sinh, hồ sơ tòa và giấy tờ về nước.' },
    '第三方卵子供应匹配与医学补偿': { label: 'Ghép chọn trứng hiến tặng và bồi hoàn y khoa', description: 'Sàng lọc người hiến, bồi hoàn, hồ sơ pháp lý và quản lý chu kỳ.' },
    '经认证低温库捐精采购': { label: 'Mua tinh trùng từ ngân hàng lạnh chứng nhận', description: 'Mẫu ngân hàng tinh trùng, vận chuyển và hồ sơ tương hợp gene.' },
    '孕母匹配、心理评估与机构管理': { label: 'Ghép người mang thai, đánh giá tâm lý và quản lý đơn vị', description: 'Lý lịch, sức khỏe tâm lý và sàng lọc chuyên khoa sinh sản.' },
    '爱心孕母补偿与妊娠专属医疗保险': { label: 'Bồi hoàn người mang thai và bảo hiểm thai kỳ', description: 'Bồi hoàn thai kỳ, trợ cấp sinh hoạt, bảo hiểm y tế và dự phòng chi phí.' },
    '顾问个案路径厘清与文件初审': { label: 'Cố vấn làm rõ lộ trình và rà soát hồ sơ sơ bộ', description: 'Rà soát thân phận, nguồn giao tử, người mang thai và luật điểm đến.' },
    '国际差旅、翻译与文件递送': { label: 'Đi lại quốc tế, dịch thuật và gửi hồ sơ', description: 'Đi lại cho tư vấn, lấy trứng, chuyển phôi hoặc hồ sơ khai sinh.' }
  }
};

const timelineText: Record<JourneyLocale, GuidanceReport['timeline']> = {
  'zh-Hans': defaultTimeline,
  en: [
    { phase: 'Phase 1', monthRange: 'Month 1', title: 'Fatherhood consult, semen review, and genetic file setup', description: 'Build the intended-father file with semen analysis, infectious-disease testing, genetic carrier screening, and donor/carrier requirements.' },
    { phase: 'Phase 2', monthRange: 'Months 2-3', title: 'Fertilization, blastocyst culture, and PGT-A grading', description: 'Arrange ICSI/IVF, culture embryos to blastocyst stage, and screen where legally appropriate.' },
    { phase: 'Phase 3', monthRange: 'Months 3-5', title: 'Donor or carrier matching, attorney documents, and escrow setup', description: 'Review donor/sperm/carrier records, psychology, contracts, and escrow account setup.' },
    { phase: 'Phase 4', monthRange: 'Months 6-14', title: 'Transfer, pregnancy monitoring, birth documents, and travel papers', description: 'Complete transfer and prenatal care under physician guidance, with legal team handling birth, parentage, and travel documents.' }
  ],
  vi: [
    { phase: 'Giai đoạn 1', monthRange: 'Tháng 1', title: 'Tư vấn lộ trình làm cha, tinh dịch đồ và hồ sơ di truyền', description: 'Lập hồ sơ người cha dự định với tinh dịch đồ, xét nghiệm bệnh truyền nhiễm, sàng lọc mang gene và yêu cầu người hiến/người mang thai.' },
    { phase: 'Giai đoạn 2', monthRange: 'Tháng 2-3', title: 'Thụ tinh, nuôi phôi nang và phân loại PGT-A', description: 'Sắp xếp ICSI/IVF, nuôi phôi đến giai đoạn phôi nang và sàng lọc nơi pháp luật cho phép.' },
    { phase: 'Giai đoạn 3', monthRange: 'Tháng 3-5', title: 'Ghép người hiến hoặc người mang thai, luật sư và ký quỹ', description: 'Rà soát hồ sơ người hiến/tinh trùng/người mang thai, tâm lý, hợp đồng và tài khoản ký quỹ.' },
    { phase: 'Giai đoạn 4', monthRange: 'Tháng 6-14', title: 'Chuyển phôi, theo dõi thai, giấy khai sinh và giấy tờ đi lại', description: 'Hoàn tất chuyển phôi và chăm sóc thai theo bác sĩ, cùng đội pháp lý xử lý khai sinh, quyền cha mẹ và giấy tờ đi lại.' }
  ]
};

function replaceKnownValue(value: string, locale: JourneyLocale) {
  const exact = valueText[locale][value];
  if (exact) return exact;

  const localized = value
    .replace(/未填/g, valueText[locale].未填 ?? '未填')
    .replace(/未检测/g, valueText[locale].未检测 ?? '未检测');

  if (locale === 'en') {
    return localized
      .replace(/(\d+(?:\.\d+)?)\s*岁/g, '$1 years')
      .replace(/(\d+(?:\.\d+)?)\s*枚/g, '$1 embryos');
  }

  if (locale === 'vi') {
    return localized
      .replace(/(\d+(?:\.\d+)?)\s*岁/g, '$1 tuổi')
      .replace(/(\d+(?:\.\d+)?)\s*枚/g, '$1 phôi');
  }

  return localized;
}

function localizeReport(report: GuidanceReport, preferences: UserPreferences, medical: MedicalInputs, locale: JourneyLocale): GuidanceReport {
  if (locale === 'zh-Hans') return report;

  const missingData = getMissingData(preferences, medical, locale);
  return {
    ...report,
    summary:
      locale === 'en'
        ? report.validationFlags.length > 0
          ? 'Customized parenthood plan and cross-border legal summary (preliminary assessment)'
          : 'Customized parenthood plan and cross-border legal summary'
        : report.validationFlags.length > 0
          ? 'Tóm tắt lộ trình sinh sản cá nhân hóa và pháp lý xuyên biên giới (đánh giá sơ bộ)'
          : 'Tóm tắt lộ trình sinh sản cá nhân hóa và pháp lý xuyên biên giới',
    missingData,
    countryFits: report.countryFits.map((country) => ({
      ...country,
      country: countryText[locale][country.code]?.country ?? country.enName,
      summary: countryText[locale][country.code]?.summary ?? country.summary,
      legalNote: countryText[locale][country.code]?.legalNote ?? country.legalNote,
      reasons: country.reasons.map((reason) => reasonText[locale][reason] ?? reason)
    })),
    biomarkerFeedback: report.biomarkerFeedback.map((item) => {
      const localized = feedbackText[locale][item.label] ?? {};
      return {
        ...item,
        ...localized,
        value: localized.value ?? replaceKnownValue(item.value, locale),
        text: localized.text ?? item.text,
        advice: localized.advice ?? item.advice
      };
    }),
    costEstimate: {
      ...report.costEstimate,
      baselineCountry: locale === 'en' ? 'Median across comparable countries' : 'Trung vị của các quốc gia có thể so sánh',
      lines: report.costEstimate.lines.map((line) => ({
        ...line,
        label: costText[locale][line.label]?.label ?? line.label,
        description: costText[locale][line.label]?.description ?? line.description
      }))
    },
    timeline: timelineText[locale],
    nextSteps:
      locale === 'en'
        ? [
            missingData.length ? `Complete missing data: ${missingData.join(', ')}.` : 'Keep current medical records ready for physician and lab review.',
            preferences.twinPreference ? 'Twin goals require physician review of multiple pregnancy risk and cannot be treated as guaranteed.' : 'Compare every country using the same access, service-scope, total-budget, and document criteria; no country is preselected.',
            preferences.sexSelectionRequested ? 'Sex preference can only be reviewed where law and medical ethics allow embryo screening.' : 'Keep the baby goal as planning input, not as medical or legal feasibility.',
            'If surrogacy or third-party gametes are involved, complete legal access review before paying major deposits.',
            'Save the planning result and submit private intake for one-on-one document review.'
          ]
        : [
            missingData.length ? `Bổ sung dữ liệu còn thiếu: ${missingData.join(', ')}.` : 'Giữ hồ sơ y khoa hiện tại để bác sĩ và labo rà soát.',
            preferences.twinPreference ? 'Mục tiêu song thai cần bác sĩ đánh giá rủi ro đa thai và không thể xem là kết quả cam kết.' : 'So sánh mọi quốc gia theo cùng tiêu chí về tiếp cận, phạm vi dịch vụ, tổng ngân sách và hồ sơ; không chọn sẵn quốc gia nào.',
            preferences.sexSelectionRequested ? 'Mong muốn giới tính chỉ được rà soát ở nơi luật và đạo đức y khoa cho phép sàng lọc phôi.' : 'Giữ mục tiêu em bé như dữ liệu lập kế hoạch, không đồng nghĩa khả thi y khoa hoặc pháp lý.',
            'Nếu liên quan mang thai hộ hoặc giao tử bên thứ ba, hoàn tất rà soát pháp lý trước khi đặt cọc lớn.',
            'Lưu kết quả và gửi đăng ký riêng để hẹn rà soát hồ sơ một-một.'
          ],
    boundaryNote:
      locale === 'en'
        ? 'This report is rules-based preliminary information. It does not promise embryo sex, twins, clinical success, or legal access, and cannot replace physician diagnosis, attorney advice, or final local authority review.'
        : 'Báo cáo này chỉ là thông tin sơ bộ theo quy tắc. Không cam kết giới tính phôi, song thai, tỷ lệ thành công hoặc khả năng pháp lý, và không thay thế chẩn đoán bác sĩ, ý kiến luật sư hoặc phê duyệt cơ quan địa phương.'
  };
}

export function buildGuidanceReportForLocale(preferences: UserPreferences, medical: MedicalInputs, locale: JourneyLocale | 'zh-Hant' = 'zh-Hans'): GuidanceReport {
  const normalizedLocale = normalizeJourneyLocale(locale);
  const pathway = deriveRecommendedPathway(preferences, medical);
  const validationFlags = getValidationFlags(preferences, medical);
  const missingData = getMissingData(preferences, medical, 'zh-Hans');
  const countryFits = rankCountries(preferences, validationFlags);
  const comparableCosts = countryFits
    .filter((country) => country.status === 'ideal' || country.status === 'allowed' || country.status === 'needs_review')
    .map((country) => country.averageCostUSD)
    .sort((left, right) => left - right);
  const medianComparableCost = comparableCosts.length
    ? comparableCosts[Math.floor(comparableCosts.length / 2)]
    : countryFits[0].averageCostUSD;

  const report: GuidanceReport = {
    summary: validationFlags.length > 0 ? '客制化求子方案与跨域法规集成总结（初步评估）' : '客制化求子方案与跨域法规集成总结',
    recommendedPathway: pathway,
    validationFlags,
    missingData,
    biomarkerFeedback: evaluateBiomarkers(preferences, medical),
    countryFits,
    costEstimate: estimateCosts(preferences, pathway, medianComparableCost),
    timeline: defaultTimeline,
    nextSteps: [
      missingData.length ? `补齐缺失资料：${missingData.join('、')}。` : '保留目前医疗资料，交由医师与实验室复核。',
      preferences.twinPreference ? '双胎或龙凤胎愿景需先由医师确认多胎妊娠风险，不能作为承诺性结果。' : '用同一组准入、服务范围、总预算与文件标准比较所有国家，不预设优先国家。',
      preferences.sexSelectionRequested ? '性别偏好仅能在法律与医疗伦理允许地区进行文件与胚胎筛查核对。' : '保留宝宝愿景作为目标输入，不直接等同于医疗或法律可行性。',
      '如涉及代孕或第三方配子，先完成律师准入核对再支付大额定金。',
      '保存本次规划结果，提交顾问登记以安排一对一文件审核。'
    ],
    boundaryNote: '本报告为规则化初步资讯整理，不承诺胚胎性别、双胎结果、临床成功率或法律准入，不能替代医师诊断、律师意见或当地主管机关最终审核。'
  };

  return localizeReport(report, preferences, medical, normalizedLocale);
}

export function formatUSD(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`;
}
